'use server'

import { apiRequest, ApiResponse } from '../request'

/**
 * Portal do cliente (/client).
 *
 * Não há endpoint de listagem próprio: `GET /consultations|exams|orders`
 * já devolvem só os atendimentos do cliente quando o token é de um usuário
 * CLIENT (`getClientScope` no backend). Aqui ficam apenas as chamadas que o
 * portal tem de exclusivo — o pagamento flex.
 */

/**
 * Visão do atendimento no portal do cliente. Projeção curada do backend: mostra
 * tudo do procedimento MENOS valores discriminados (repasse, custo, preço por
 * item) — só totais e as cobranças do próprio cliente.
 */
export interface ClientAppointmentView {
    id: number
    title: string
    status: string
    flexPayment: boolean
    /** Token do link público (/p/<token>). Só presente em procedimento flex. */
    publicToken: string | null
    /** Nome do paciente (exibido no topo do detalhe). */
    clientName: string | null
    /** Primeiro nome do paciente (saudação do link público). */
    clientFirstName: string | null
    scheduledAt: string | null
    arrivalOrder: boolean
    duration: number | null
    completedAt: string | null
    preInstructions: string | null
    postInstructions: string | null
    professional: { name: string; registrationNumber: string | null } | null
    team: { name: string; registrationNumber: string | null }[]
    partner: { name: string } | null
    hospital: { name: string } | null
    healthPlan: { name: string } | null
    room: string | null
    address: {
        street: string | null
        number: string | null
        complement: string | null
        neighborhood: string | null
        city: string | null
        state: string | null
        zipCode: string | null
    } | null
    /** Venda de pacote: a tela troca rótulos ("Exames" não são exames). */
    isPackage?: boolean
    supplies: { name: string; quantity: number; unit: string | null }[]
    /**
     * Atrelados ao atendimento. Num PACOTE são os COMPONENTES (a cirurgia, a consulta do
     * anestesista, o retorno) — a tela troca o título por causa disso.
     */
    exams: { name: string }[]
    items: { name: string; quantity: number }[]
    /** Total do procedimento (valor de venda), em centavos. */
    finalPrice: number
    /** Soma das cobranças geradas (não canceladas), em centavos. */
    billedSoFar: number
    /** Soma do que já foi PAGO, em centavos. */
    paidSoFar: number
    /** "Falta" = total − pago, em centavos. */
    remaining: number
    /** Teto para gerar nova cobrança = total − já gerado, em centavos. */
    remainingToBill: number
    charges: {
        id: number
        amount: number
        status: string
        paidAmount: number | null
        dueDate: string | null
        paidAt: string | null
        publicToken: string | null
    }[]
}

export async function getClientAppointmentView(
    orderId: number
): Promise<ApiResponse<ClientAppointmentView>> {
    return apiRequest<ClientAppointmentView>(
        `orders/${orderId}/client-view`,
        {}
    )
}

export interface FlexBalance {
    /** Valor total do procedimento, em centavos. */
    finalPrice: number
    /** Soma das cobranças já emitidas (âncora + parciais não canceladas). */
    billedSoFar: number
    /** Soma do que já foi pago, em centavos. */
    paidSoFar: number
    /** "Falta" = total − pago, em centavos. */
    remaining: number
    /** Teto para gerar nova cobrança = total − já gerado, em centavos. */
    remainingToBill: number
    flexPayment: boolean
}

export async function getFlexBalance(
    orderId: number
): Promise<ApiResponse<FlexBalance>> {
    return apiRequest<FlexBalance>(`orders/${orderId}/flex-balance`, {})
}

export interface FlexPaymentResult {
    billingId: number
    /** Token do checkout da cobrança criada (rota /pay/:token). */
    checkoutToken: string | null
}

export async function createFlexPayment(
    orderId: number,
    data: { amount: number; dueDate?: string }
): Promise<ApiResponse<FlexPaymentResult>> {
    return apiRequest<FlexPaymentResult>(
        `orders/${orderId}/flex-payment`,
        { data },
        'post'
    )
}

// ============================================================================
// Link público do procedimento (/p/:token) — SEM login, CPF confirma
// ============================================================================

/** Marca da empresa para o portão de CPF (nada do paciente antes de validar). */
export async function getPublicOrderMeta(
    token: string
): Promise<ApiResponse<{ companyName: string | null }>> {
    return apiRequest<{ companyName: string | null }>(
        `orders/p/${token}/meta`,
        {}
    )
}

/** Sessão do link público: visão do atendimento + token curto (JWT). */
export interface PublicOrderSession {
    /** JWT curto bound a este link; guardar por link no sessionStorage. */
    accessToken: string
    view: ClientAppointmentView
}

/** Confirma o CPF, devolve a visão curada e o token de sessão. */
export async function verifyPublicOrder(
    token: string,
    cpf: string
): Promise<ApiResponse<PublicOrderSession>> {
    return apiRequest<PublicOrderSession>(
        `orders/p/${token}/view`,
        { data: { cpf } },
        'post'
    )
}

/** Retoma a sessão pelo token (sem CPF) — ex.: ao voltar do checkout. */
export async function resumePublicOrder(
    token: string,
    accessToken: string
): Promise<ApiResponse<{ view: ClientAppointmentView }>> {
    return apiRequest<{ view: ClientAppointmentView }>(
        `orders/p/${token}/resume`,
        { data: { accessToken } },
        'post'
    )
}

/** Cria um pagamento avulso pelo link público (autoriza por sessão ou CPF). */
export async function createPublicFlexPayment(
    token: string,
    data: {
        cpf?: string
        accessToken?: string
        amount: number
        dueDate?: string
    }
): Promise<ApiResponse<FlexPaymentResult>> {
    return apiRequest<FlexPaymentResult>(
        `orders/p/${token}/flex-payment`,
        { data },
        'post'
    )
}
